import {
  FormSourceRef,
  FormSourceRefKind,
  PaymentSourceKind,
} from "@evergiving/common/dist/es2015/pledge";

import { RootState } from "@/lib/redux/types";

import {
  PledgePaymentAuthorizationState,
  looksLikeAnAuthorization,
} from "@/lib/misc/pledgePaymentAuthorization";

/**
 * What the `i` parameter says. A pledge link carries a signed token holding
 * both the pledge it names and the amount it authorises; every other link
 * carries a plain ref. "invalid" is a link that named something and failed:
 * the donor sees the Expired Link page rather than a donation form.
 */
export type FormLink =
  | { kind: "none" }
  | { kind: "invalid" }
  | {
      kind: "resolved";
      ref: FormSourceRef;
      authorization?: PledgePaymentAuthorizationState;
    };

const plainRef = (candidate: string): FormSourceRef | undefined => {
  const match = candidate.match(/^([it])(\d+)$/);

  if (!match) {
    return undefined;
  }

  return {
    kind:
      match[1] === "t"
        ? FormSourceRefKind.TeamMemberRef
        : FormSourceRefKind.DigitalFormInstanceRef,
    id: Number(match[2]),
  };
};

const decodeToken = (
  candidate: string,
  now: Date
): PledgePaymentAuthorizationState | undefined => {
  const parts = candidate.split(".");

  if (parts.length !== 2 || !parts[0]) {
    return undefined;
  }

  let authorization: unknown;

  try {
    authorization = JSON.parse(
      Buffer.from(decodeURIComponent(parts[0]), "base64").toString()
    );
  } catch {
    return undefined;
  }

  if (!looksLikeAnAuthorization(authorization)) {
    return undefined;
  }

  // An expired token is not a link the donor can use. Rails normally redirects
  // such a link before the form loads; if one arrives anyway it is invalid,
  // not a form with a dead amount.
  if (Date.parse(authorization.expiresAt) <= now.getTime()) {
    return undefined;
  }

  return { token: candidate, authorization };
};

/**
 * Reads the `i` parameter. The client holds no signing key, so nothing here is
 * verified: form-submit decides whether a token is genuine.
 */
export function formLinkFromQueryParam(
  instance: string | string[] | undefined,
  now: Date = new Date()
): FormLink {
  if (instance === undefined) {
    return { kind: "none" };
  }

  const asArray = Array.isArray(instance) ? instance : [instance];
  const candidate = asArray.find((value) => value.length > 0);

  if (!candidate) {
    return { kind: "none" };
  }

  const ref = plainRef(candidate);

  if (ref) {
    return { kind: "resolved", ref };
  }

  const authorization = decodeToken(candidate, now);

  if (!authorization) {
    return { kind: "invalid" };
  }

  return {
    kind: "resolved",
    ref: {
      kind: FormSourceRefKind.PledgeRef,
      id: authorization.authorization.pledgeId,
    },
    authorization,
  };
}

/**
 * The only payment source this link authorises, if it names one. A link with
 * no token, or a token with no payment source, restricts nothing.
 */
export const authorizedPaymentSourceSelector = (
  state: RootState
): PaymentSourceKind | undefined =>
  state.formPledgePaymentAuthorization?.authorization.paymentSource;

/**
 * Whether the donor may still change what they give. A pledge link signs the
 * amount and the frequency into its token, so neither can be altered and
 * there is no earlier page to send the donor back to.
 */
export const canChangeDonationSelector = (state: RootState): boolean =>
  state.formPledgePaymentAuthorization === undefined;
